import React from 'react';
import { Info, Receipt, FileText, Calculator, DollarSign, AlertTriangle } from 'lucide-react';

/**
 * Компонент с информацией о суточных и командировках
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Всё о суточных и командировках в России
      </h2>
      
      <div className="prose prose-indigo dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Receipt className="w-5 h-5 text-indigo-500" />
          Что такое суточные (командировочные)?
        </h3>
        <p>
          <strong>Суточные</strong> — это денежная компенсация работнику, направленному в командировку, предназначенная для оплаты дополнительных расходов, связанных с проживанием вне места постоянного жительства (питание, мелкие бытовые услуги и т.д.). Суточные выплачиваются за каждый день нахождения в командировке, включая выходные и праздничные дни, а также за дни нахождения в пути.
        </p>

        <h3 className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-indigo-500" />
          Нормативы суточных
        </h3>
        <p>
          Законодательство РФ не устанавливает минимальный или максимальный размер суточных. Размер суточных определяется локальными нормативными актами организации. Однако Налоговый кодекс РФ устанавливает необлагаемый НДФЛ лимит суточных:
        </p>
        <ul>
          <li><strong>700 рублей</strong> — для командировок по территории РФ</li>
          <li><strong>2500 рублей</strong> — для командировок за пределы РФ</li>
        </ul>
        <p>
          Сумма суточных, превышающая указанные лимиты, облагается НДФЛ по ставке 13%.
        </p>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium">Важно о документальном оформлении</h4>
          <p className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            Для командировок необходимо оформление следующих документов: приказ о направлении в командировку, командировочное удостоверение (с 2015 года не обязательно, но многие организации продолжают использовать), служебное задание и отчет о его выполнении, авансовый отчет.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-indigo-500" />
          Порядок расчета суточных
        </h3>
        <p>
          Суточные выплачиваются за каждый день нахождения в командировке, включая:
        </p>
        <ul>
          <li>День выезда в командировку</li>
          <li>Все дни пребывания в месте командирования, включая выходные и праздничные дни</li>
          <li>День возвращения из командировки</li>
        </ul>
        <p>
          При однодневных командировках порядок выплаты суточных устанавливается организацией самостоятельно. Если работник может ежедневно возвращаться к месту постоянного жительства, суточные не выплачиваются (если иное не установлено локальными актами компании).
        </p>

        <h3 className="flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-green-500" />
          Другие расходы при командировках
        </h3>
        <p>
          Помимо суточных, работодатель обязан возместить сотруднику:
        </p>
        <ul>
          <li><strong>Расходы на проезд</strong> до места командировки и обратно, включая страховые платежи, оплату услуг по оформлению проездных документов, расходы за пользование в поездах постельными принадлежностями</li>
          <li><strong>Расходы на проживание</strong>, включая бронирование жилого помещения</li>
          <li><strong>Дополнительные расходы</strong>, связанные со служебной командировкой и согласованные с работодателем</li>
        </ul>
        <p>
          Все расходы, кроме суточных, должны быть подтверждены документально (билеты, посадочные талоны, чеки, квитанции и т.д.).
        </p>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Особенности зарубежных командировок
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>В день пересечения границы РФ суточные выплачиваются в иностранной валюте по нормам страны командирования</li>
            <li>В день возвращения из-за границы суточные выплачиваются по российским нормам</li>
            <li>Даты пересечения границы определяются по отметкам в загранпаспорте или проездным документам</li>
            <li>Необходимо оформление командировочного удостоверения (хотя законодательно это не обязательно с 2015 года)</li>
            <li>Рекомендуется наличие медицинской страховки для выезда за рубеж</li>
          </ul>
        </div>

        <h3>Налогообложение командировочных расходов</h3>
        <p>
          С точки зрения налогообложения, командировочные расходы делятся на две категории:
        </p>
        <ol>
          <li><strong>Не облагаемые НДФЛ в полном объеме</strong> при наличии подтверждающих документов:
            <ul>
              <li>Расходы на проезд</li>
              <li>Расходы на проживание</li>
              <li>Сборы за услуги аэропортов, комиссия за покупку билетов и т.п.</li>
            </ul>
          </li>
          <li><strong>Не облагаемые НДФЛ в пределах норм</strong>:
            <ul>
              <li>Суточные в пределах 700 руб. (РФ) и 2500 руб. (зарубежные)</li>
              <li>Суммы сверх норм подлежат налогообложению НДФЛ (13%)</li>
            </ul>
          </li>
        </ol>

        <h3>Полезные советы по командировкам</h3>
        <ul>
          <li>Храните все билеты, посадочные талоны, чеки и квитанции</li>
          <li>Составьте отчет о командировке сразу после возвращения</li>
          <li>Авансовый отчет нужно сдать в течение 3 рабочих дней после возвращения</li>
          <li>Если вы потеряли документы, можно подтвердить расходы служебной запиской и косвенными доказательствами</li>
          <li>При заграничных командировках меняйте валюту через официальные обменники и сохраняйте справки</li>
          <li>В случае болезни в командировке период временной нетрудоспособности не включается в срок командировки</li>
        </ul>

        <h3>Правовая база</h3>
        <p>
          Основные документы, регулирующие командировки в РФ:
        </p>
        <ul>
          <li>Трудовой кодекс РФ (глава 24, статьи 166-169)</li>
          <li>Постановление Правительства РФ от 13.10.2008 № 749 "Об особенностях направления работников в служебные командировки"</li>
          <li>Налоговый кодекс РФ (ст. 217 п. 3)</li>
          <li>Постановление Правительства РФ от 02.10.2002 № 729 (устанавливает размер возмещения для госслужащих, может использоваться как ориентир коммерческими организациями)</li>
        </ul>

        <p className="font-semibold mt-6">
          Наш калькулятор суточных поможет вам рассчитать командировочные расходы в соответствии с законодательством РФ, учитывая все нормативы и правила налогообложения!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;